import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Audit Log Document ---

export interface AuditLog {
  id: string;
  tenant_id: string;
  user_id: string;
  action: string;
  resource: string;
  resource_id: string;
  details: string;
  ip_address: string;
  user_agent: string;
  timestamp: string;
}

// --- Audit Config Document ---

export interface AuditConfig {
  id: string;
  tenant_id: string;
  resource: string;
  actions: string[];
  is_enabled: boolean;
  retention_days: number;
  log_details: boolean;
  notify_on_action: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request Types ---

export interface CreateAuditConfigRequest {
  resource: string;
  actions: string[];
  is_enabled?: boolean;
  retention_days?: number;
  log_details?: boolean;
  notify_on_action?: boolean;
}

export interface UpdateAuditConfigRequest {
  actions: string[];
  is_enabled?: boolean;
  retention_days?: number;
  log_details?: boolean;
  notify_on_action?: boolean;
}

// --- Filter Types ---

export interface AuditLogFilters extends SearchParams {
  action?: string;
  resource?: string;
  user_id?: string;
}

export interface AuditConfigFilters extends SearchParams {
  resource?: string;
}

export interface AuditSummaryParams {
  resource?: string;
  date_from?: string;
  date_to?: string;
}

// --- Summary Response ---

export interface AuditSummary {
  total: number;
  by_action: Array<{ action: string; count: number }>;
  by_resource: Array<{ resource: string; count: number }>;
}

// --- Audit Log API (read-only) ---

export const getAuditLogs = (params: AuditLogFilters = {}) =>
  apiGet<PagedResponse<AuditLog>>('/api/v1/audit-logs', params);

export const getAuditLog = (id: string) =>
  apiGet<AuditLog>(`/api/v1/audit-logs/${id}`);

export const getAuditSummary = (params: AuditSummaryParams = {}) =>
  apiGet<AuditSummary>('/api/v1/audit-logs/summary', params);

// --- Audit Config API (CRUD) ---

export const getAuditConfigs = (params: AuditConfigFilters = {}) =>
  apiGet<PagedResponse<AuditConfig>>('/api/v1/audit-configs', params);

export const getAuditConfig = (id: string) =>
  apiGet<AuditConfig>(`/api/v1/audit-configs/${id}`);

export const createAuditConfig = (data: CreateAuditConfigRequest) =>
  apiPost<AuditConfig>('/api/v1/audit-configs', data);

export const updateAuditConfig = (id: string, data: UpdateAuditConfigRequest) =>
  apiPut<AuditConfig>(`/api/v1/audit-configs/${id}`, data);

export const deleteAuditConfig = (id: string) =>
  apiDelete<void>(`/api/v1/audit-configs/${id}`);
